import { hooks, state } from "@springtree/eva-sdk-react-recoil";
import { useQuery } from "@tanstack/react-query";
import classNames from "classnames";
import { AnimatePresence, motion } from "framer-motion";
import Image from "next/image";
import { useTranslations } from "next-intl";
import { ReactNode, useEffect, useRef, useState } from "react";
import whatInput from "what-input";

import { useConfiguration } from "~/contexts/configuration";
import { getDiscountedAndOOSProductsByIds } from "~/services/algolia-utils";
import Algolia from "~/types/algolia";
import CrossCountry from "~/types/cross-country";
import { basicAnimationStates } from "~/utils/animation-utils";
import Constants from "~/utils/constants";
import imageUtils from "~/utils/image-utils";
import intlUtils from "~/utils/intl-utils";
import productUtils from "~/utils/product-utils";

import styles from "./product-colors-dialog.module.scss";

export type PropsProductColor = {
  product: CrossCountry.ChildrenProduct;
  onSelect: (product: CrossCountry.ChildrenProduct) => void;
  onMouseEnter?: () => void;
  hasFocus?: boolean;
  children?: ReactNode;
  isGift: boolean;
  isHighlighted?: boolean;
  notifyMe?: boolean;
};

export function ProductColor({
  product,
  onSelect,
  onMouseEnter,
  hasFocus,
  children,
  isGift,
  isHighlighted,
  notifyMe,
}: PropsProductColor) {
  const goccia = productUtils.getProductDroplet(product?.product_media?.media);
  const listItem = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    // set focus to the highlighted option
    if ((isHighlighted && hasFocus) || notifyMe) {
      listItem.current?.focus();
    }
  }, [isHighlighted, hasFocus, notifyMe]);

  return (
    <button
      ref={listItem}
      role="option"
      key={product.product_id}
      id={product.product_id + "_color"}
      className={classNames(
        styles.color,
        isHighlighted && styles.activeColor,
        !product.has_stock && !isGift ? styles.outOfStock : undefined
      )}
      onClick={() => onSelect(product)}
      aria-selected={isHighlighted}
      onMouseEnter={onMouseEnter}
      // tabIndex={isHighlighted ? 0 : -1}
    >
      <div className={styles.imageContainer}>
        <Image
          alt={product.color || ""}
          src={goccia?.url || imageUtils.transparentPixel}
          width={20}
          height={20}
          loader={imageUtils.evaImageLoader}
          aria-hidden
        />
      </div>
      {children}
    </button>
  );
}

export type PropsProductColorsDialog = {
  current: CrossCountry.ChildrenProduct;
  products: CrossCountry.ChildrenProduct[];
  onSelect: (product: CrossCountry.ChildrenProduct) => void;
  onChange: () => void;
  onClose: () => void;
  showColorList?: boolean;
  isGift?: boolean;
  isAlgolia?: boolean;
  backendId?: string;
};

export default function ProductColorsDialog({
  current,
  products,
  onSelect,
  onChange,
  onClose,
  showColorList,
  isGift = false,
  isAlgolia = false,
  backendId,
}: PropsProductColorsDialog) {
  const t = useTranslations();
  const priceFormatter = intlUtils.usePriceFormatter();

  const [highlightedIndex, setHighlightedIndex] = useState<number>(0);
  const [hasFocus, setHasFocus] = useState<boolean>(false);
  const [notifyMe, setNotifyMe] = useState<boolean>(false);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (!showColorList) return;

    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlightedIndex((prevIndex) => (prevIndex + 1) % products.length);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlightedIndex((prevIndex) => (prevIndex - 1 + products.length) % products.length);
    } else if (e.key === "Enter") {
      // select and close the dialog
      e.preventDefault();
      setHighlightedIndex((index) => index);
      onSelect(products[highlightedIndex]);
      if (products[highlightedIndex].has_stock) {
        onClose();
      } else {
        setNotifyMe(true);
      }
    }
  };
  const { eva } = useConfiguration();
  const evaEndpointUrl = hooks.useGetState(state.core.evaEndpointUrlState);
  const [newProducts, setNewProducts] = useState(products);

  const { data: discountedAndOOSProducts, isFetching } = useQuery<Algolia.DiscountedAndOOSProducts>({
    queryKey: ["algoliaProductColorsDialogAvailabilityAndDiscounts", backendId],
    staleTime: Constants.DEFAULT_PLP_STALE_TIME,
    keepPreviousData: true,
    queryFn: async () => {
      return (
        (await getDiscountedAndOOSProductsByIds(
          eva,
          newProducts.map((p) => p.product_id.toString())
        )) ?? {}
      );
    },
    enabled: isAlgolia && !!backendId && !!evaEndpointUrl && newProducts.length > 0,
  });

  useEffect(() => {
    if (discountedAndOOSProducts && Object.values(discountedAndOOSProducts).length > 0) {
      setNewProducts((prevState) =>
        prevState?.map((child) => {
          const foundProductOOS = discountedAndOOSProducts[child.product_id];

          const is_available = foundProductOOS?.isAvailable ?? child.is_available;
          const has_stock = foundProductOOS?.hasStock ?? child.has_stock;
          const discounts = foundProductOOS?.discounts ?? [];

          return {
            ...child,
            is_available,
            has_stock,
            discounts,
          };
        })
      );
    }
  }, [discountedAndOOSProducts]);

  return (
    <AnimatePresence key={`listenabled-${showColorList}`}>
      <motion.div
        initial={basicAnimationStates.off}
        animate={basicAnimationStates.on}
        exit={basicAnimationStates.off}
        role="listbox"
        aria-labelledby="listbox-panel"
        aria-activedescendant={current.product_id + "_color"}
        className={classNames(styles.content, { [styles.singleColor]: !showColorList })}
        onKeyDown={handleKeyDown}
        onFocus={() => setHasFocus(true)}
      >
        {showColorList ? (
          newProducts?.map((product, index) => (
            <ProductColor
              key={product.product_id}
              product={product}
              isGift={isGift}
              onSelect={() => (!isFetching ? onSelect(product) : undefined)}
              isHighlighted={highlightedIndex === index && whatInput.ask() == "keyboard"}
              onMouseEnter={() => (whatInput.ask() == "keyboard" ? setHighlightedIndex(index) : "")}
              hasFocus={hasFocus}
            >
              <p className={styles.colorName}>{product.color}</p>
              <p className={styles.price}>
                {priceFormatter.currency(product.display_price, { currency: product.currency_id })}
              </p>
            </ProductColor>
          ))
        ) : (
          <ProductColor
            key={current.product_id}
            product={current}
            onSelect={() => onChange()}
            isGift={isGift}
            isHighlighted
            notifyMe={notifyMe}
          >
            <p className={styles.colorName}>{current.color}</p>
            <p className={styles.changeButton}>{t("generic.changecount", { count: newProducts.length })}</p>
          </ProductColor>
        )}
      </motion.div>
    </AnimatePresence>
  );
}
